import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { TASK_TYPES, toLocalInput } from '@/lib/taskUtils';

const NONE = '__none__';
const cls = 'bg-white border-gray-300';

function LinkPicker({ form, setForm }) {
  const [leads, setLeads] = useState([]);
  const [buyers, setBuyers] = useState([]);
  useEffect(() => {
    API.get('/leads').then(r => setLeads(r.data)).catch(() => {});
    API.get('/buyers').then(r => setBuyers(r.data)).catch(() => {});
  }, []);
  const value = form.buyer_id ? `b:${form.buyer_id}` : form.lead_id ? `l:${form.lead_id}` : NONE;
  const onChange = (v) => setForm(p => ({ ...p, lead_id: v.startsWith('l:') ? v.slice(2) : null, buyer_id: v.startsWith('b:') ? v.slice(2) : null }));
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid="task-link-select" className={cls}><SelectValue placeholder="Link to lead or buyer" /></SelectTrigger>
      <SelectContent className="bg-white max-h-72">
        <SelectItem value={NONE}>No link</SelectItem>
        {leads.map(l => <SelectItem key={l.id} value={`l:${l.id}`}>Lead · {l.property_address || l.name}</SelectItem>)}
        {buyers.map(b => <SelectItem key={b.id} value={`b:${b.id}`}>Buyer · {b.name}{b.email || b.phone ? ` (${b.email || b.phone})` : ''}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export function TaskDialog({ open, onOpenChange, task, leadId, buyerId, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const fixedLink = !!(leadId || buyerId);

  useEffect(() => {
    if (!open) return;
    setForm(task ? { ...task, due_local: toLocalInput(task.due_at) }
      : { type: 'call', title: '', due_local: toLocalInput(), notes: '', lead_id: leadId || null, buyer_id: buyerId || null });
  }, [open, task, leadId, buyerId]);

  const up = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.title?.trim()) { toast.error('Add a title'); return; }
    setSaving(true);
    const payload = { type: form.type, title: form.title, notes: form.notes || '', due_at: new Date(form.due_local).toISOString() };
    try {
      if (task) await API.put(`/tasks/${task.id}`, payload);
      else await API.post('/tasks', { ...payload, lead_id: form.lead_id, buyer_id: form.buyer_id });
      toast.success(task ? 'Task updated' : 'Task created');
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save task'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-md" data-testid="task-dialog">
        <DialogHeader><DialogTitle>{task ? 'Edit Task' : 'New Task'}</DialogTitle></DialogHeader>
        <div className="flex gap-1.5">
          {Object.entries(TASK_TYPES).map(([k, t]) => {
            const Icon = t.icon;
            return (
              <button key={k} data-testid={`task-type-${k}`} onClick={() => up('type', k)}
                className={`flex-1 flex flex-col items-center gap-1 py-2 rounded-lg border text-xs font-medium transition-colors ${form.type === k ? 'border-[#0073ea] bg-blue-50 text-[#0073ea]' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                <Icon className="h-4 w-4" />{t.label}
              </button>
            );
          })}
        </div>
        <div className="space-y-1.5"><Label className="text-xs text-gray-500">Title</Label>
          <Input data-testid="task-title-input" value={form.title || ''} onChange={e => up('title', e.target.value)} placeholder="e.g. Call agent about counter offer" className={cls} /></div>
        <div className="space-y-1.5"><Label className="text-xs text-gray-500">Due</Label>
          <Input data-testid="task-due-input" type="datetime-local" value={form.due_local || ''} onChange={e => up('due_local', e.target.value)} className={cls} /></div>
        {!fixedLink && !task && <div className="space-y-1.5"><Label className="text-xs text-gray-500">Linked to</Label><LinkPicker form={form} setForm={setForm} /></div>}
        <div className="space-y-1.5"><Label className="text-xs text-gray-500">Notes</Label>
          <Textarea data-testid="task-notes-input" value={form.notes || ''} onChange={e => up('notes', e.target.value)} rows={3} className={cls} /></div>
        <Button data-testid="task-save-btn" onClick={save} disabled={saving} className="bg-[#0073ea] hover:bg-[#0060c2] text-white">{saving ? 'Saving...' : task ? 'Save' : 'Create Task'}</Button>
      </DialogContent>
    </Dialog>
  );
}
